/* eslint-disable multiline-ternary */
/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  ButtonGroup,
  Center,
  Flex,
  Grid,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Heading,
  IconButton,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Select,
  Switch,
  Text,
  type CenterProps,
  GridItem,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tooltip,
  InputGroup
} from '@chakra-ui/react'
import { AnimatePresence, motion } from 'motion/react'
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
  type ChangeEvent
} from 'react'
import ReactPlayer from 'react-player'
import { MultiInput } from '../playalong/modules'

import {
  blankEpisode,
  getYoutubeOEmbedAPIUrl,
  getYoutubeChannelDetailsAPIUrl,
  getYouTubeVideoId,
  slugify,
  getTwitchVideoDetailsAPIUrl,
  getTwitchOUserDetailsAPIUrl,
  TWITCH_HTTP_API_KEY,
  TWITCH_CLIENT_ID
} from './utils'
import {
  Pause,
  ChevronLeft,
  ChevronRight,
  CircleX,
  ClockArrowDown,
  ClockArrowUp,
  Copy,
  CirclePlus,
  FastForward,
  Info,
  Trash2,
  GitPullRequestArrow
} from 'lucide-react'
import {
  type Episode,
  type Event,
  type TieredAnswer,
  type MusicSource,
  TOTAL_TIME,
  ANSWER_TIME,
  RESULTS_TIME,
  SCORES_TIME
} from '../playalong/types'

import { Link } from '../../components/Link'
const PLACEHOLDER_QUESTION = 'Placeholder'
const MAX_RESULT_TIME = TOTAL_TIME

enum AppStates {
  INTRO,
  LOADING,
  SETUP,
  IMPORT,
  FETCH_INFO,
  INFO,
  DEV,
  SAVE,
  AUTOSAVE,
  EXPORT
}

const validateVideoUrl = (url: string): string => {
  if (url === '') {
    return 'Please enter a youtube / twitch URL'
  }

  const urlRegex =
    /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.?be|twitch\.tv)\/.+$/
  if (!urlRegex.test(url)) {
    return 'Please enter a valid youtube / twitch URL'
  }

  return ''
}

const cleanVideoUrl = (url: string): string => {
  try {
    const urlObj = new URL(url)
    urlObj.searchParams.delete('t')
    return urlObj.toString()
  } catch (error) {
    // If URL parsing fails, attempt basic string manipulation
    return url.replace('&t=', '&t_n=').replace('?t=', '?t_n=')
  }
}

const checkImageLoad = async (url: string): Promise<boolean> => {
  return await new Promise((resolve) => {
    const img = new Image()
    img.onload = () => {
      resolve(true)
    } // Image loaded successfully
    img.onerror = () => {
      resolve(false)
    } // Image failed to load
    img.src = url
  })
}

const questionTypeOptions = (
  <>
    <option value="stringCompare">Text Answer</option>
    <option value="numberCurve">Number Answer</option>
    {/* <option value="percentageCurve">Percentage Answer</option> */}
    <option value="multiple">Multiple Choice</option>
    <option value="order">Order</option>
    <option value="tieredAnswers">Tiered Answers</option>
  </>
)
const Page: React.FC = () => {
  const [appState, setAppState] = useState<AppStates>(AppStates.INTRO)
  const [addPromptType, setAddPromptType] = useState<string>('stringCompare')
  const [addPromptPoints, setAddPromptPoints] = useState<number | undefined>(10)
  const [videoUrl, setVideoUrl] = useState<string>('')
  const [videoProvider, setVideoProvider] = useState<string>()
  const [seriesName, setSeriesName] = useState<string>('')
  const [creatorName, setCreatorName] = useState<string>('')
  const [creatorLearnMoreLink, setCreatorLearnMoreLink] = useState<string>('')
  const [episodeTitle, setEpisodeTitle] = useState<string>('')

  const [errorMessage, setErrorMessage] = useState<string>('')
  const [toDelete, setToDelete] = useState<string>()
  const episodeRef = useRef<Episode>({ ...blankEpisode })
  const [selectedEventIndex, setSelectedEventIndex] = useState<number>(-1)
  const [selectedUUID, setSelectedUUID] = useState<string>()
  const videoRef = useRef<ReactPlayer>(null)
  const [refreshCounter, setForceRefresh] = useState<number>(0)
  const refreshTimeout = useRef<NodeJS.Timeout>()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [showResetModal, setShowResetModal] = useState<boolean>(false)
  const [tabIndex, setTabIndex] = useState<number>(0)
  const [promptMusic, setPromptMusic] = useState<MusicSource>({
    source: 'none',
    url: '',
    startTime: 0,
    endTime: 1
  })
  const [introMusic, setIntroMusic] = useState<MusicSource>({
    source: 'none',
    url: '',
    startTime: 0,
    endTime: 1
  })

  const forceRefresh = useCallback((): void => {
    clearTimeout(refreshTimeout.current)
    refreshTimeout.current = setTimeout(() => {
      console.log('forceRefresh')
      setForceRefresh((prev) => prev + 1)
    }, 100)
  }, [])
  const forceRefreshForButtons = useCallback((): void => {
    clearTimeout(refreshTimeout.current)
    refreshTimeout.current = setTimeout(() => {
      console.log('forceRefresh')
      setForceRefresh((prev) => prev + 1)
    }, 200)
  }, [])

  useEffect(() => {
    if (selectedEventIndex >= 1) {
      setSelectedUUID(selectedPrompt.id)
    } else {
      setSelectedUUID(undefined)
    }
  }, [selectedEventIndex])

  const saveMusic = (): void => {
    episodeRef.current.introMusic = introMusic
    episodeRef.current.promptMusic = promptMusic
    saveEpisodeToLocalStorage()
  }

  const setCleanVideoUrl = useCallback((url: string): void => {
    setVideoUrl(cleanVideoUrl(url))
  }, [])

  const saveEpisodeToLocalStorage = (text?: string): void => {
    // console.log('saveEpisodeToLocalStorage', text ?? 'no text')
    forceRefresh()
    try {
      const episodeData = JSON.stringify(episodeRef.current)
      localStorage.setItem('savedEpisode', episodeData)
    } catch (error) {
      console.error('Failed to save episode to local storage:', error)
    }
  }

  const loadEpisodeFromLocalStorage = useCallback((): void => {
    try {
      const episodeData = localStorage.getItem('savedEpisode')
      if (episodeData !== null) {
        episodeRef.current = JSON.parse(episodeData)
        if (episodeRef.current.introMusic !== undefined) {
          setIntroMusic(episodeRef.current.introMusic as MusicSource)
        }
        if (episodeRef.current.promptMusic !== undefined) {
          setPromptMusic(episodeRef.current.promptMusic as MusicSource)
        }
        sortPrompts()
        setEpisodeTitle(episodeRef.current.title)
        setSeriesName(episodeRef.current.series.name)
        setCreatorName(episodeRef.current.series?.creator?.name ?? '')

        if (episodeRef.current.videoUrl === '') {
          setAppState(AppStates.INTRO)
        } else if (episodeRef.current.title === '') {
          setCleanVideoUrl(episodeRef.current.videoUrl)
          setAppState(AppStates.INFO)
        } else {
          setCleanVideoUrl(episodeRef.current.videoUrl)
          setAppState(AppStates.DEV)
        }
      }
    } catch (error) {
      console.error('Failed to load episode from local storage:', error)
    }
  }, [])

  const playDebugEpisode = useCallback((): void => {
    saveEpisodeToLocalStorage()
    try {
      const tempEpisode = JSON.parse(
        JSON.stringify(episodeRef.current)
      ) as Episode
      tempEpisode.events.forEach((event) => {
        switch (event.type) {
          case 'multiple':
          case 'order':
            break
          default:
            delete event.choices
            break
        }
        if (event.displayAnswer === '') {
          delete event.displayAnswer
        }
        if (
          typeof event.acceptableAnswers !== 'object' ||
          event.acceptableAnswers.length === 0 ||
          event.type === 'multiple'
        ) {
          delete event.acceptableAnswers
        } else {
          delete event.answer
        }
      })
      // end

      const json = JSON.stringify(tempEpisode)
      localStorage.setItem('savedDebugEpisode', json)
      window.open('/playalong/debug', '_blank')
    } catch (error) {
      console.error('Failed to save episode to local storage:', error)
    }
  }, [])

  const addPrompt = useCallback((): void => {
    const promptId = crypto.randomUUID()

    const newPrompt = {
      id: promptId,
      startTime: Math.ceil(videoRef.current?.getCurrentTime() ?? 0),
      type: addPromptType,
      pause: true,
      question: '',
      answer: '',
      acceptableAnswers: [],
      tieredAnswers: [],
      displayAnswer: '',
      points: addPromptPoints,
      stepAmount: 1,
      stepDrop: 1,
      threshold: 0.8
    }
    const newResult = {
      id: crypto.randomUUID(),
      startTime: Math.ceil(videoRef.current?.getCurrentTime() ?? 0) + 1,
      type: 'results',
      resultsFor: promptId
    }
    const tempEvents = [...episodeRef.current.events, newPrompt, newResult]
    episodeRef.current = {
      ...episodeRef.current,
      events: tempEvents
    }
    sortPrompts()
  }, [addPromptPoints, addPromptType])

  const addNewTieredAnswer = useCallback((index: number): void => {
    if (episodeRef.current.events[index].tieredAnswers === undefined) {
      episodeRef.current.events[index].tieredAnswers = []
    }
    const resultEvent = episodeRef.current.events.find(
      (event) => event.resultsFor === episodeRef.current.events[index].id
    )
    episodeRef.current.events[index].tieredAnswers.push({
      acceptableAnswers: [],
      points: 1
    })
    if (resultEvent?.tieredResults !== undefined) {
      resultEvent.tieredResults.push(resultEvent.startTime)
    }

    saveEpisodeToLocalStorage()
  }, [])

  const removeTieredAnswer = useCallback(
    (index: number, tIndex: number): void => {
      if (episodeRef?.current?.events?.[index]?.tieredAnswers === undefined) {
        return
      }
      episodeRef.current.events[index].tieredAnswers.splice(tIndex, 1)
      const resultEvent = episodeRef.current.events.find(
        (event) => event.resultsFor === episodeRef.current.events[index].id
      )
      if (resultEvent?.tieredResults !== undefined) {
        resultEvent.tieredResults.splice(tIndex, 1)
      }

      saveEpisodeToLocalStorage()
    },
    []
  )

  const setTieredResultTime = useCallback(
    (eventId: string, index: number): void => {
      const resultEvent = episodeRef.current.events.find(
        (event) => event.id === eventId
      )
      const promptEvent = episodeRef.current.events.find(
        (event) => event.id === resultEvent?.resultsFor
      )

      if (
        resultEvent === undefined ||
        promptEvent?.tieredAnswers === undefined
      ) {
        return
      }

      if (resultEvent?.tieredResults === undefined) {
        resultEvent.tieredResults = Array(
          promptEvent.tieredAnswers.length
        ).fill((promptEvent.endTime ?? promptEvent.startTime) + 1)
      }
      if (resultEvent?.tieredResults?.[index] !== undefined) {
        resultEvent.tieredResults[index] = Math.ceil(
          videoRef.current?.getCurrentTime() ?? 0
        )
        const tempResults = structuredClone(resultEvent.tieredResults).sort(
          (a, b) => a - b
        )
        resultEvent.startTime = tempResults[0]
        resultEvent.endTime = tempResults[tempResults.length - 1] + 20
      }
      saveEpisodeToLocalStorage()
    },
    []
  )

  const seekToPrompt = useCallback(
    (promptId: string): void => {
      const prompt = episodeRef.current.events.find(
        (event) => event.id === promptId
      )

      if (prompt !== undefined) {
        const wasPlaying = getVideoState() === 1
        videoPlay()
        if (videoRef.current?.getCurrentTime() !== prompt.startTime) {
          videoSeekTo(prompt.startTime)
        } else if (
          prompt.endTime !== undefined &&
          videoRef.current?.getCurrentTime() !== prompt.endTime
        ) {
          videoSeekTo(prompt.endTime)
        } else if (prompt.resultsFor !== undefined) {
          let endTime = prompt.endTime ?? prompt.startTime + TOTAL_TIME + 1
          if (prompt.type === 'tieredAnswers') {
            endTime = prompt.tieredResults?.[0] ?? endTime
          }
          if (prompt.type === 'quickResults') {
            endTime = prompt.startTime + ANSWER_TIME + 1
          }
          videoSeekTo(endTime)
        }
        videoPause()
        if (wasPlaying) {
          setTimeout(() => {
            videoPlay()
          }, 100)
        }
      }
    },
    [episodeRef.current.events, videoProvider]
  )

  const seekToTime = useCallback((time: number): void => {
    const wasPlaying = getVideoState() === 1

    videoPlay()
    videoSeekTo(time)
    videoPause()
    if (wasPlaying) {
      videoPlay()
    }
  }, [])

  const duplicatePrompt = useCallback(
    (promptId: string, duplicateTo: 'after' | 'at'): void => {
      const promptIndex = episodeRef.current.events.findIndex(
        (event) => event.id === promptId
      )
      const prompt = structuredClone(episodeRef.current.events[promptIndex])
      const newPrompt = {
        ...prompt,
        id: crypto.randomUUID(),
        startTime:
          duplicateTo === 'after'
            ? (prompt.endTime ?? prompt.startTime) + 1
            : Math.ceil(videoRef.current?.getCurrentTime() ?? 0),
        endTime:
          duplicateTo === 'after'
            ? prompt.endTime === undefined
              ? undefined
              : prompt.endTime + 1
            : undefined,
        question:
          (typeof prompt.question === 'string' && prompt.question !== ''
            ? prompt.question
            : PLACEHOLDER_QUESTION) + ' (Copy)'
      }
      const newResult = {
        id: crypto.randomUUID(),
        startTime: newPrompt.startTime + 2,
        type: 'results',
        resultsFor: newPrompt.id
      }
      const newUUID = newPrompt.id
      const tempEvents = [...episodeRef.current.events, newPrompt, newResult]
      episodeRef.current = {
        ...episodeRef.current,
        events: tempEvents
      }
      sortPrompts()
      setSelectedEventIndex(-1)
      setSelectedUUID(newUUID)
      forceRefresh()
    },
    [episodeRef.current.events]
  )

  const setEpisodeStartTime = useCallback((): void => {
    episodeRef.current.startTime = Math.ceil(
      videoRef.current?.getCurrentTime() ?? 0
    )
    sortPrompts()
  }, [episodeRef.current.events, videoRef.current])

  const setPromptStartTime = useCallback((): void => {
    if (selectedEventIndex !== -1) {
      selectedPrompt.startTime = Math.ceil(
        videoRef.current?.getCurrentTime() ?? 0
      )
      const tempEvents = episodeRef.current.events.sort(
        (a, b) => a.startTime - b.startTime
      )
      episodeRef.current = {
        ...episodeRef.current,
        events: tempEvents
      }

      setSelectedEventIndex(
        tempEvents.findIndex((event) => event.id === selectedUUID) + 1
      )
      sortPrompts()
    }
  }, [episodeRef.current.events, selectedEventIndex, selectedUUID])

  const setPromptEndTime = useCallback((): void => {
    if (selectedEventIndex !== -1) {
      selectedPrompt.endTime = Math.ceil(
        videoRef.current?.getCurrentTime() ?? 0
      )
      const tempEvents = episodeRef.current.events.sort(
        (a, b) => a.startTime - b.startTime
      )
      episodeRef.current = {
        ...episodeRef.current,
        events: tempEvents
      }

      if (selectedPrompt.resultsFor === undefined) {
        const resultEvent = episodeRef.current.events.find(
          (event) => event.resultsFor === selectedPrompt.id
        )
        if (resultEvent !== undefined) {
          resultEvent.startTime = Math.max(
            resultEvent.startTime,
            selectedPrompt.endTime + 1
          )
        }
      }
      setSelectedEventIndex(
        tempEvents.findIndex((event) => event.id === selectedUUID) + 1
      )
      sortPrompts()
    }
  }, [episodeRef.current.events, selectedEventIndex, selectedUUID])

  const clearPromptEndTime = useCallback((): void => {
    if (selectedEventIndex !== -1) {
      selectedPrompt.endTime = undefined
      const tempEvents = episodeRef.current.events.sort(
        (a, b) => a.startTime - b.startTime
      )
      episodeRef.current = {
        ...episodeRef.current,
        events: tempEvents
      }
      setSelectedEventIndex(
        tempEvents.findIndex((event) => event.id === selectedUUID) + 1
      )
      sortPrompts()
    }
  }, [episodeRef.current.events, selectedEventIndex, selectedUUID])

  const removePrompt = useCallback(
    (promptId: string): void => {
      let tempEvents = episodeRef.current.events.filter(
        (event) => event.id !== promptId
      )
      tempEvents = tempEvents.filter(
        (event) =>
          event.resultsFor === undefined || event.resultsFor !== promptId
      )
      episodeRef.current = {
        ...episodeRef.current,
        events: tempEvents
      }
      sortPrompts()
      setSelectedEventIndex(-1)
      setSelectedUUID(undefined)
    },
    [episodeRef.current.events]
  )

  useEffect(() => {
    loadEpisodeFromLocalStorage()
  }, [])

  useEffect(() => {
    if (videoUrl.includes('youtube.com')) {
      setVideoProvider('youtube')
    } else if (videoUrl.includes('twitch.tv')) {
      setVideoProvider('twitch')
    }
  }, [videoUrl])

  const handleImportClick = useCallback((): void => {
    if (fileInputRef.current != null) {
      fileInputRef.current.click() // Programmatically open the file input
    }
  }, [fileInputRef.current])

  const handleImportFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      const file = event.target.files !== null ? event.target.files[0] : null
      if (file !== null) {
        const reader = new FileReader()
        reader.onload = (e) => {
          try {
            if (e.target === null || e.target.result === null) {
              throw new Error('No file loaded')
            }
            const tempEpisode = JSON.parse(e.target.result as string) // Parse JSON content
            episodeRef.current = tempEpisode
            sortPrompts()
            if (episodeRef.current.videoUrl === '') {
              setAppState(AppStates.IMPORT)
            } else if (episodeRef.current.title === '') {
              setCleanVideoUrl(episodeRef.current.videoUrl)
              setAppState(AppStates.INFO)
            } else {
              setCleanVideoUrl(episodeRef.current.videoUrl)
              setAppState(AppStates.DEV)
            }

            if (fileInputRef.current != null) {
              fileInputRef.current.value = '' // Reset the file input
            }
          } catch (error) {
            console.error('Error parsing JSON:', error)
            alert('Invalid JSON file.')
          }
        }
        reader.readAsText(file) // Read the file as text
      }
    },
    [fileInputRef.current]
  )

  const exportEpisodeData = useCallback((): void => {
    // sanitize start
    const tempEpisode = JSON.parse(
      JSON.stringify(episodeRef.current)
    ) as Episode
    tempEpisode.events.forEach((event) => {
      switch (event.type) {
        case 'multiple':
        case 'order':
          break
        default:
          delete event.choices
          break
      }
      if (event.displayAnswer === '') {
        delete event.displayAnswer
      }
      if (
        typeof event.acceptableAnswers !== 'object' ||
        event.acceptableAnswers.length === 0 ||
        event.type === 'multiple'
      ) {
        delete event.acceptableAnswers
      } else {
        delete event.answer
      }
      if (event.type !== 'tieredResults') {
        delete event.tieredResults
      }
    })
    // end

    const json = JSON.stringify(tempEpisode)
    const blob = new Blob([json], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a') // Create a link element
    const fileName = slugify(
      `playalong-${creatorName}-${episodeRef.current.title}`
    )

    link.href = url
    link.download = `${fileName}.json` // Set the download filename
    link.click() // Trigger the download

    URL.revokeObjectURL(url) // Clean up the URL object
  }, [])

  const resetEpisode = useCallback((): void => {
    episodeRef.current = { ...blankEpisode }
    sortPrompts()
    setCleanVideoUrl('')
    setAppState(AppStates.SETUP)
    saveEpisodeToLocalStorage()
  }, [episodeRef.current.events])

  const updateResultTypes = useCallback((): void => {
    episodeRef.current.events.forEach((event) => {
      if (event.tieredResults !== undefined && event.tieredResults.length > 0) {
        event.type = 'tieredResults'
      }
    })
  }, [episodeRef.current.events])

  const fetchYoutubeVideoInfo = useCallback((): void => {
    fetch(getYoutubeOEmbedAPIUrl(videoUrl) ?? '')
      .then(async (response) => {
        if (!response.ok) {
          setErrorMessage('Youtube Api Not Working Correctly, try again')
          setAppState(AppStates.SETUP)
          // throw new Error('Network response was not ok ' + response.statusText)
        }
        return await response.json()
      })
      .then(async (data) => {
        const hasMaxresdefault = await checkImageLoad(
          data.thumbnail_url.replace('hqdefault', 'maxresdefault')
        )
        if (hasMaxresdefault) {
          data.thumbnail_url = data.thumbnail_url.replace(
            'hqdefault',
            'maxresdefault'
          )
        } else {
          data.thumbnail_url = data.thumbnail_url.replace(
            'hqdefault',
            'sddefault'
          )
        }
        const youtubeUsername = data.author_url.split('@')[1] as string
        setEpisodeTitle(data.title)
        setSeriesName('')
        setCreatorName(data.author_name)
        setCreatorLearnMoreLink('https://www.youtube.com/@' + youtubeUsername)
        episodeRef.current = {
          ...episodeRef.current,
          title: data.title,
          videoUrl,
          videoPreview: data.thumbnail_url,
          series: {
            ...episodeRef.current.series,
            name: '',
            creator: {
              ...episodeRef.current.series.creator,
              name: data.author_name,
              qrCode: 'https://www.youtube.com/@' + youtubeUsername,
              platforms: {
                ...episodeRef.current.series.creator.platforms,
                content: {
                  youtube: data.author_url
                }
              }
            }
          }
        }
        saveEpisodeToLocalStorage()
        fetch(getYoutubeChannelDetailsAPIUrl(youtubeUsername))
          .then(async (response) => {
            if (!response.ok) {
              setErrorMessage(
                'Youtube Channel Details Api Not Working Correctly, try again'
              )
              setAppState(AppStates.SETUP)
              // throw new Error('Network response was not ok ' + response.statusText)
            }
            return await response.json()
          })
          .then(async (data) => {
            const channelIcon = data.items[0].snippet.thumbnails.medium.url
            episodeRef.current = {
              ...episodeRef.current,
              series: {
                ...episodeRef.current.series,
                creator: {
                  ...episodeRef.current.series.creator,
                  description: data.items[0].snippet.description,
                  channelIcon
                }
              }
            }
            saveEpisodeToLocalStorage()
            setAppState(AppStates.INFO)
          })
          .catch((error) => {
            console.error(
              'There was a problem with the second (channel info) fetch operation:',
              error
            )
          })
      })
      .catch((error) => {
        console.error(
          'There was a problem with the first (video info) fetch operation:',
          error
        )
      })
  }, [videoUrl])

  const fetchTwitchVideoInfo = useCallback((): void => {
    fetch(getTwitchVideoDetailsAPIUrl(videoUrl) ?? '', {
      headers: {
        Authorization: 'Bearer ' + TWITCH_HTTP_API_KEY,
        'Client-Id': TWITCH_CLIENT_ID
      }
    })
      .then(async (response) => {
        if (!response.ok) {
          setErrorMessage('Twitch Api Not Working Correctly, try again')
          setAppState(AppStates.SETUP)
          // throw new Error('Network response was not ok ' + response.statusText)
        }
        return await response.json()
      })
      .then(async ({ data }) => {
        data = data[0]
        setEpisodeTitle(data.title)
        setSeriesName('')
        setCreatorName(data.user_name)
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        setCreatorLearnMoreLink('https://www.twitch.tv/' + data.user_name)
        episodeRef.current = {
          ...episodeRef.current,
          title: data.title,
          videoUrl,
          videoPreview: data.thumbnail_url
            .replace('%{width}', '1280')
            .replace('%{height}', '720'),
          series: {
            ...episodeRef.current.series,
            name: '',
            creator: {
              ...episodeRef.current.series.creator,
              name: data.user_name,
              // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
              qrCode: 'https://www.twitch.tv/' + data.user_name,
              platforms: {
                ...episodeRef.current.series.creator.platforms,
                content: {
                  // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                  twitch: 'https://www.twitch.tv/' + data.user_name
                }
              }
            }
          }
        }
        saveEpisodeToLocalStorage()
        fetch(getTwitchOUserDetailsAPIUrl(data.user_name), {
          headers: {
            Authorization: 'Bearer ' + TWITCH_HTTP_API_KEY,
            'Client-Id': TWITCH_CLIENT_ID
          }
        })
          .then(async (response) => {
            if (!response.ok) {
              setErrorMessage(
                'Twitch Channel Details Api Not Working Correctly, try again'
              )
              setAppState(AppStates.SETUP)
              // throw new Error('Network response was not ok ' + response.statusText)
            }
            return await response.json()
          })
          .then(async ({ data }) => {
            data = data[0]
            const channelIcon = data.profile_image_url
            episodeRef.current = {
              ...episodeRef.current,
              series: {
                ...episodeRef.current.series,
                creator: {
                  ...episodeRef.current.series.creator,
                  description: data.description,
                  channelIcon
                }
              }
            }
            saveEpisodeToLocalStorage()
            setAppState(AppStates.INFO)
          })
          .catch((error) => {
            console.error(
              'There was a problem with the second (channel info) fetch operation:',
              error
            )
          })
      })
      .catch((error) => {
        console.error(
          'There was a problem with the first (video info) fetch operation:',
          error
        )
      })
  }, [videoUrl])

  const getVideoState = useCallback((): number | null => {
    if (videoProvider === 'youtube') {
      return videoRef.current?.getInternalPlayer().getPlayerState()
    } else if (videoProvider === 'twitch') {
      switch (videoRef.current?.getInternalPlayer().getPlayerState().playback) {
        case 'Ready':
          return 0
        case 'Playing':
          return 1
        default:
          return null
      }
    }
    return null
  }, [videoProvider])

  const videoSeekTo = useCallback(
    (time: number): void => {
      if (videoProvider === 'youtube') {
        videoRef.current?.getInternalPlayer().seekTo(time)
      } else if (videoProvider === 'twitch') {
        videoRef.current?.getInternalPlayer().seek(time)
      }
    },
    [videoProvider]
  )

  const videoPlay = useCallback((): void => {
    if (videoProvider === 'youtube') {
      videoRef.current?.getInternalPlayer().playVideo()
    } else if (videoProvider === 'twitch') {
      videoRef.current?.getInternalPlayer().play()
    }
  }, [videoProvider])

  const videoPause = useCallback((): void => {
    if (videoProvider === 'youtube') {
      videoRef.current?.getInternalPlayer().pauseVideo()
    } else if (videoProvider === 'twitch') {
      videoRef.current?.getInternalPlayer().pause()
    }
  }, [videoProvider])

  const sortPrompts = useCallback((): void => {
    console.log('sortPrompts')
    let finalResultIndex = episodeRef.current.events.findIndex(
      (event) => event.type === 'finalResults'
    )
    if (finalResultIndex === -1) {
      const finalResults = {
        id: crypto.randomUUID(),
        startTime: videoRef.current?.getDuration() ?? Infinity,
        type: 'finalResults'
      }
      episodeRef.current = {
        ...episodeRef.current,
        events: [...episodeRef.current.events, finalResults]
      }
      finalResultIndex = episodeRef.current.events.length - 1
    }
    updateResultTypes()
    if (episodeRef.current.events.length === 1) {
      saveEpisodeToLocalStorage()
      return
    }

    if (finalResultIndex !== episodeRef.current.events.length - 1) {
      episodeRef.current.events[finalResultIndex].startTime = Infinity
      episodeRef.current = {
        ...episodeRef.current,
        events: [...episodeRef.current.events].sort(
          (a, b) => a.startTime - b.startTime
        )
      }
      finalResultIndex = episodeRef.current.events.findIndex(
        (event) => event.type === 'finalResults'
      )
    }

    const lastPrompt =
      episodeRef.current.events[episodeRef.current.events.length - 2]
    const prevEndTime =
      lastPrompt.endTime ??
      (lastPrompt.type === 'quickResults'
        ? lastPrompt.startTime + ANSWER_TIME + 1
        : lastPrompt.startTime + MAX_RESULT_TIME + 1)
    if (
      episodeRef.current.events[finalResultIndex].startTime <=
        prevEndTime + 1 ||
      episodeRef.current.events[finalResultIndex].startTime === Infinity
    ) {
      episodeRef.current.events[finalResultIndex].startTime = prevEndTime + 1
    }

    if (episodeRef.current.endTime === 0) {
      episodeRef.current.endTime = videoRef.current?.getDuration() ?? 0
    }

    episodeRef.current.totalPrompts =
      episodeRef.current.events.filter(
        (event) => event.resultsFor === undefined
      ).length - 1

    saveEpisodeToLocalStorage()
  }, [episodeRef.current.events])

  useEffect(() => {
    switch (appState) {
      case AppStates.LOADING:
        setAppState(AppStates.INTRO)
        break
      case AppStates.SETUP:
        break
      case AppStates.FETCH_INFO:
        if (videoUrl.includes('youtube.com')) {
          if (getYoutubeOEmbedAPIUrl(videoUrl) === null) {
            setErrorMessage('Invalid Youtube URL')
            setAppState(AppStates.SETUP)
          } else {
            fetchYoutubeVideoInfo()
            setVideoProvider('youtube')
          }
        } else if (videoUrl.includes('twitch.tv')) {
          if (getTwitchVideoDetailsAPIUrl(videoUrl) === null) {
            setErrorMessage('Invalid Twitch URL')
            setAppState(AppStates.SETUP)
          } else {
            fetchTwitchVideoInfo()
            setVideoProvider('twitch')
          }
        } else {
          setErrorMessage('Invalid Video URL')
          setAppState(AppStates.SETUP)
        }
        break
      case AppStates.INFO:
        break
      case AppStates.DEV:
        break
      case AppStates.SAVE:
        break
      case AppStates.AUTOSAVE:
        break
      case AppStates.EXPORT:
        break
      default:
        break
    }
  }, [appState, setAppState])

  const selectedPrompt =
    episodeRef.current.events[selectedEventIndex - 1] ?? undefined
  const videoCurrentTime = Math.ceil(videoRef.current?.getCurrentTime() ?? 0)
  const toDeletePrompt = episodeRef.current.events.find(
    (event) => event.id === toDelete
  )
  const toDeleteResulstFor = episodeRef.current.events.find(
    (event) => event.id === toDeletePrompt?.resultsFor
  )
  const resultsForPrompt =
    selectedPrompt !== undefined
      ? episodeRef.current.events.find(
          (event) => event.id === selectedPrompt.resultsFor
        )
      : undefined
  const secondToLastPrompt =
    episodeRef.current.events[episodeRef.current.events.length - 2]

  const disableSetResultStartTime =
    selectedEventIndex <= 0 ||
    (resultsForPrompt?.endTime ?? 0) >= videoCurrentTime ||
    (resultsForPrompt?.startTime ?? 0) > videoCurrentTime

  const disableFinalResultsStartTime =
    selectedEventIndex <= 0 ||
    secondToLastPrompt === undefined ||
    (secondToLastPrompt?.endTime ?? 0) >= videoCurrentTime ||
    (secondToLastPrompt.type === 'quickResults' &&
      secondToLastPrompt.startTime + ANSWER_TIME >= videoCurrentTime) ||
    (secondToLastPrompt.type === 'results' &&
      secondToLastPrompt.startTime + MAX_RESULT_TIME >= videoCurrentTime)

  const disableSetPromptStartTime =
    selectedEventIndex <= 0 ||
    (selectedPrompt?.endTime ?? Infinity) < videoCurrentTime
  const disableSetPromptEndTime =
    selectedEventIndex >= 1 && selectedPrompt.startTime > videoCurrentTime

  const eventList = useMemo(() => {
    const promptNumberObject: Record<string, number> = {}
    let promptNumber = 0
    return episodeRef.current.events.map((event, index) => {
      let promptNumberString = ''
      let resultFor: any | undefined
      const prevPrompt = episodeRef.current.events[index - 1]
      let prevEndTime: number = -1
      let isOverlapping = false
      const isOpen = selectedEventIndex === index + 1

      if (event.resultsFor !== undefined) {
        resultFor = episodeRef.current.events.find(
          (e) => e.id === event.resultsFor
        )
      }

      const setEventType = (type: string): void => {
        event.type = type
        if (resultFor === undefined) {
          return
        }

        switch (type) {
          case 'tieredAnswers':
            resultFor.type = 'tieredResults'
            break
          default:
            resultFor.type = 'quickResults'
            break
        }
      }

      if (prevPrompt !== undefined) {
        switch (prevPrompt?.type) {
          case 'results':
            prevEndTime = prevPrompt.startTime + MAX_RESULT_TIME + 1
            break
          case 'quickResults':
            prevEndTime = prevPrompt.startTime + ANSWER_TIME + 1
            break
          default:
            prevEndTime = prevPrompt?.endTime ?? prevPrompt.startTime
            break
        }
      }
      if (event.startTime <= prevEndTime) {
        isOverlapping = true
      }
      if (event.startTime < episodeRef.current.startTime) {
        isOverlapping = true
      }
      if (resultFor !== undefined) {
        if (
          resultFor.startTime > event.startTime ||
          (resultFor.endTime !== undefined &&
            resultFor.endTime > event.startTime)
        ) {
          isOverlapping = true
        }
        promptNumberString =
          'R' +
          (promptNumberObject[resultFor.id as string]?.toString() ?? 'error')
      } else {
        promptNumber += 1
        promptNumberObject[event.id] = promptNumber

        promptNumberString = promptNumber.toString()
      }

      const startTimeString = new Date(event.startTime * 1000)
        .toISOString()
        .slice(11, 19)
        .replace(/^00:/, '')
      let endTimeString =
        event.endTime !== undefined
          ? new Date(event.endTime * 1000)
              .toISOString()
              .slice(11, 19)
              .replace(/^00:/, '')
          : ''

      if (event.type === 'finalResults') {
        endTimeString = new Date(episodeRef.current.endTime * 1000)
          .toISOString()
          .slice(11, 19)
          .replace(/^00:/, '')
        return (
          <AccordionItem
            key={event.id}
            _expanded={{
              backgroundColor: 'yellow.100'
            }}
          >
            <Flex
              sx={{
                bg: isOverlapping ? 'red.100' : 'transparent',
                '&:has(.chakra-accordion__button[aria-expanded=true])': {
                  backgroundColor: 'yellow.100'
                }
              }}
            >
              <AccordionButton>
                <Flex
                  as="span"
                  flex="1"
                  textAlign="left"
                  gap="1em"
                  sx={{
                    div: {
                      minW: '4em',
                      textAlign: 'center'
                    }
                  }}
                >
                  <Box></Box>
                  <Box color={isOverlapping ? 'red.500' : 'black'}>
                    {startTimeString}
                  </Box>
                  <Box>{endTimeString}</Box>
                  <Box>Final Results</Box>
                </Flex>
                <AccordionIcon />
              </AccordionButton>
              <ButtonGroup mr="20px" gap="0.5em">
                <Box
                  as="button"
                  onClick={(e: any) => {
                    e.stopPropagation()
                    seekToPrompt(event.id)
                  }}
                  aria-label="Seek To Prompt"
                >
                  <FastForward />
                </Box>
              </ButtonGroup>
            </Flex>
            <AccordionPanel pb={4}>
              Final Results Cannot be removed or placed before any other prompts
            </AccordionPanel>
          </AccordionItem>
        )
      }

      if (resultFor !== undefined) {
        let endTime = event.endTime ?? event.startTime + 21
        if (event.type === 'tieredAnswers') {
          endTime = event.tieredResults?.[0] ?? endTime
        }
        if (event.type === 'quickResults') {
          endTime = event.startTime + 6
        }

        endTimeString = new Date(endTime * 1000)
          .toISOString()
          .slice(11, 19)
          .replace(/^00:/, '')

        return (
          <AccordionItem
            key={event.id}
            sx={
              isOverlapping
                ? {
                    bg: 'red.100',
                    fontWeight: 'bold'
                  }
                : {}
            }
            _expanded={{
              bg: 'yellow.100'
            }}
          >
            <Flex
              sx={{
                bg: isOverlapping ? 'red.100' : 'transparent',
                '&:has(.chakra-accordion__button[aria-expanded=true])': {
                  backgroundColor: 'yellow.100'
                }
              }}
            >
              <AccordionButton>
                <Flex
                  as="span"
                  flex="1"
                  textAlign="left"
                  gap="1em"
                  sx={{
                    div: {
                      minW: '4em',
                      textAlign: 'center'
                    }
                  }}
                >
                  <Box
                    textAlign="center"
                    color={isOverlapping ? 'red.500' : 'black'}
                  >
                    {promptNumberString}
                  </Box>
                  <Box color={isOverlapping ? 'red.500' : 'black'}>
                    {startTimeString}
                  </Box>
                  <Box color={isOverlapping ? 'red.500' : 'black'}>
                    {endTimeString}
                  </Box>
                  <Box style={{ textAlign: 'left' }}>
                    Results for &quot;
                    {resultFor.question === ''
                      ? PLACEHOLDER_QUESTION
                      : resultFor.question}
                    &quot;
                  </Box>
                </Flex>
                <AccordionIcon />
              </AccordionButton>
              <ButtonGroup mr="20px" gap="0.5em">
                <Tooltip label="Open Parent Prompt">
                  <Box
                    as="button"
                    onClick={(e: any) => {
                      setSelectedEventIndex(
                        episodeRef.current.events.findIndex(
                          (prevEvent) => event.resultsFor === prevEvent.id
                        ) + 1
                      )
                      setSelectedUUID(event.resultsFor)
                    }}
                    aria-label="Open Parent Prompt"
                  >
                    <GitPullRequestArrow />
                  </Box>
                </Tooltip>
                <Tooltip label="Jump to Prompt">
                  <Box
                    as="button"
                    onClick={(e: any) => {
                      e.stopPropagation()
                      seekToPrompt(event.id)
                    }}
                    aria-label="Seek To Prompt"
                  >
                    <FastForward />
                  </Box>
                </Tooltip>
              </ButtonGroup>
            </Flex>
            <AccordionPanel pb={4}>
              <FormControl>
                {resultFor.type !== 'tieredAnswers' ? (
                  <>
                    <FormLabel>Result Type</FormLabel>
                    <Select
                      size={{ base: 'xs', '2xl': 'sm' }}
                      value={episodeRef.current.events[index].type}
                      onChange={(e) => {
                        episodeRef.current.events[index].type = e.target.value
                        saveEpisodeToLocalStorage()
                      }}
                    >
                      <option value="results">
                        Full Results (Answer, Top Guess & Current Total)
                      </option>
                      <option value="quickResults">
                        Quick Results (Just Show the Answer)
                      </option>
                    </Select>
                  </>
                ) : (
                  <>
                    <FormLabel>Tiered Results</FormLabel>
                    <Grid templateColumns="1fr 1fr 1fr 1fr">
                      {(resultFor.tieredAnswers ?? []).map(
                        (tieredAnswer: any, index: number) => {
                          let time = 'Not Set'
                          if (event.tieredResults?.[index] !== undefined) {
                            time = new Date(
                              (event.tieredResults?.[index] ??
                                event.startTime) * 1000
                            )
                              .toISOString()
                              .slice(11, 19)
                              .replace(/^00:/, '')
                          }

                          return (
                            <Box
                              key={event.id + index.toString()}
                              display="contents"
                            >
                              <GridItem>
                                {tieredAnswer.acceptableAnswers?.[0] ??
                                  'Missing Answer'}
                              </GridItem>
                              <GridItem>{tieredAnswer.points}</GridItem>
                              <GridItem>{time}</GridItem>
                              <GridItem>
                                <Button
                                  variant="solidBuilder"
                                  size="sm"
                                  disabled={disableSetResultStartTime}
                                  onClick={() => {
                                    setTieredResultTime(event.id, index)
                                  }}
                                >
                                  <Box as={ClockArrowDown} mr="0.75em" />
                                  Start
                                </Button>
                              </GridItem>
                            </Box>
                          )
                        }
                      )}
                    </Grid>
                  </>
                )}
              </FormControl>
            </AccordionPanel>
          </AccordionItem>
        )
      }

      return (
        <AccordionItem
          key={event.id}
          sx={
            isOverlapping
              ? {
                  bg: 'red.100',
                  fontWeight: 'bold'
                }
              : {}
          }
          _expanded={{
            bg: 'yellow.100'
          }}
        >
          <Flex
            sx={{
              '&:has(.chakra-accordion__button[aria-expanded=true])': {
                backgroundColor: 'yellow.100'
              }
            }}
          >
            <AccordionButton>
              <Flex
                as="span"
                flex="1"
                textAlign="left"
                gap="1em"
                sx={{
                  div: {
                    minW: '4em',
                    textAlign: 'center'
                  }
                }}
              >
                <Box
                  textAlign="center"
                  color={isOverlapping ? 'red.500' : 'black'}
                >
                  {promptNumberString}
                </Box>
                <Box
                  sx={{
                    color: isOverlapping ? 'red.500' : 'black'
                  }}
                >
                  {startTimeString}
                </Box>
                <Box
                  sx={{
                    color: isOverlapping ? 'red.500' : 'black'
                  }}
                >
                  {event.endTime !== undefined ? (
                    <>
                      {endTimeString}
                      <br />
                      <FormControl
                        display="flex"
                        flexDirection={{
                          base: 'column',
                          '2xl': 'row'
                        }}
                        alignItems="center"
                        mb="0 !important"
                      >
                        <FormLabel
                          htmlFor={`pb-${event.id}`}
                          mr="0"
                          mb="0"
                          textAlign="center"
                          w="100%"
                        >
                          <Pause
                            style={{
                              margin: '0 auto'
                            }}
                          />
                        </FormLabel>
                        <Switch
                          id={`pb-${event.id}`}
                          isChecked={
                            event.pause === true || event.endTime === undefined
                          }
                          size="sm"
                          onChange={(e) => {
                            episodeRef.current.events[index].pause =
                              e.currentTarget.checked
                            saveEpisodeToLocalStorage()
                          }}
                        />
                      </FormControl>
                    </>
                  ) : (
                    <Box as={Pause} w="1.5em" h="1.5em" mx="auto" />
                  )}
                </Box>
                <Box style={{ textAlign: 'left' }}>
                  &quot;
                  {event.question === ''
                    ? PLACEHOLDER_QUESTION
                    : event.question}
                  &quot;
                </Box>
              </Flex>

              <AccordionIcon />
            </AccordionButton>
            <ButtonGroup mr="20px" gap="0.5em">
              <Box
                as="button"
                aria-label="Remove Prompt"
                onClick={(e: any) => {
                  e.stopPropagation()
                  setToDelete(event.id)
                }}
              >
                <Trash2 />
              </Box>
              <Box
                as="button"
                onClick={(e: any) => {
                  e.stopPropagation()
                  duplicatePrompt(event.id, 'after')
                }}
                aria-label="Duplicate Prompt"
              >
                <Copy />
              </Box>
              <Tooltip label="Open Results Prompt">
                <Box
                  as="button"
                  onClick={(e: any) => {
                    setSelectedEventIndex(
                      episodeRef.current.events.findIndex(
                        (prevEvent) => prevEvent.resultsFor === event.id
                      ) + 1
                    )
                    setSelectedUUID(event.resultsFor)
                  }}
                  aria-label="Open Results Prompt"
                >
                  <GitPullRequestArrow style={{ rotate: '180deg' }} />
                </Box>
              </Tooltip>
              <Box
                as="button"
                onClick={(e: any) => {
                  e.stopPropagation()
                  seekToPrompt(event.id)
                }}
                aria-label="Seek To Prompt"
              >
                <FastForward />
              </Box>
            </ButtonGroup>
          </Flex>
          {isOpen && (
            <EventPanel
              key={event.id}
              episodeRef={episodeRef}
              event={event}
              index={index}
              setEventType={setEventType}
              addNewTieredAnswer={addNewTieredAnswer}
              removeTieredAnswer={removeTieredAnswer}
              saveEpisodeToLocalStorage={saveEpisodeToLocalStorage}
            />
          )}
        </AccordionItem>
      )
    })
  }, [episodeRef.current.events, selectedEventIndex, refreshCounter])

  return (
    <Box sx={{ ...styles.container }}>
      <AnimatePresence>
        {/* Setup Section */}
        <Wrap
          key="Appstate.INTRO"
          display={appState === AppStates.INTRO ? 'flex' : 'none'}
          sx={{ ...styles.intro }}
        >
          <Box height="100%" maxW="800px">
            <Heading>Playalong Builder: Beta</Heading>
            <Text fontSize="18px" lineHeight="1.75" textAlign="left">
              Hi and welcome to the Playalong Builder. This is a work in
              progress and will be updated regularly with new features and
              improvements to the overall experience.
            </Text>
            <Text fontSize="18px" lineHeight="1.75" textAlign="left">
              I will always do my best to not break compatibility with existing
              episodes, but I cannot guarantee it. However, if you do encounter
              an issue, please let me know in my{' '}
              <Box
                as="a"
                href="https://discord.com/invite/z2uZC4mFed"
                target="_blank"
                textDecoration="underline"
                rel="noreferrer"
              >
                Discord server
              </Box>{' '}
              and I will do my best to help you.
            </Text>
            <ButtonGroup>
              <Button
                variant="solidBuilder"
                w="100%"
                mt="2em"
                onClick={() => {
                  setAppState(AppStates.SETUP)
                }}
              >
                Get Started
              </Button>
              <Button
                as="a"
                variant="solidBuilder"
                w="100%"
                mt="2em"
                href="https://www.youtube.com/watch?v=zAsH3DYweCs"
                target="_blank"
              >
                Watch Tutorial
              </Button>
            </ButtonGroup>
          </Box>
        </Wrap>
        {/* Setup Section */}
        <Wrap
          key="Appstate.SETUP"
          display={appState === AppStates.SETUP ? 'flex' : 'none'}
          sx={{ ...styles.setup }}
        >
          <Box height="100%">
            <Heading>Playalong Builder: Beta</Heading>
            <Text>
              <strong>STEP 1:</strong> Enter Youtube Url Here
              <br />
              (You can also import existing episodes JSON files)
            </Text>

            <FormControl isInvalid={errorMessage.length > 0}>
              <FormLabel>Youtube URL</FormLabel>
              <Input
                size={{ base: 'xs', '2xl': 'sm' }}
                type="text"
                value={videoUrl}
                onChange={(e) => {
                  setCleanVideoUrl(e.target.value)
                }}
                onBlur={(e) => {
                  setErrorMessage(validateVideoUrl(e.target.value))
                }}
                placeholder="https://www.youtube.com/watch?v=..."
              />
              <FormHelperText>
                Enter the URL of the video you want to playalong with
              </FormHelperText>
              {errorMessage !== '' && (
                <FormErrorMessage>{errorMessage}</FormErrorMessage>
              )}
            </FormControl>
            <ButtonGroup>
              <Button
                variant="solidBuilder"
                w="100%"
                mt="2em"
                onClick={handleImportClick}
              >
                Import
              </Button>
              <Button
                variant="solidBuilder"
                w="100%"
                mt="2em"
                isDisabled={videoUrl.length === 0 || errorMessage.length > 0}
                onClick={() => {
                  setAppState(AppStates.FETCH_INFO)
                }}
              >
                Next
              </Button>
            </ButtonGroup>
          </Box>
        </Wrap>
        {/* Import Live Section */}
        <Wrap
          key="Appstate.IMPORT"
          display={appState === AppStates.IMPORT ? 'flex' : 'none'}
          sx={{ ...styles.setup }}
        >
          <Box>
            <Heading>Playalong Builder: Beta</Heading>
            <Heading>Importing Live Episode</Heading>
            <Text>
              <strong>STEP 1:</strong> Find and paste the youtube url for your
              VOD
            </Text>

            <FormControl isInvalid={errorMessage.length > 0}>
              <FormLabel>Youtube URL</FormLabel>
              <Input
                size={{ base: 'xs', '2xl': 'sm' }}
                type="text"
                value={videoUrl}
                onChange={(e) => {
                  setCleanVideoUrl(e.target.value)
                }}
                onBlur={(e) => {
                  setErrorMessage(validateVideoUrl(e.target.value))
                }}
                placeholder="https://www.youtube.com/watch?v=..."
              />
              <FormHelperText>
                Enter the VOD URL of the video you want to playalong with
              </FormHelperText>
              {errorMessage !== '' && (
                <FormErrorMessage>{errorMessage}</FormErrorMessage>
              )}
            </FormControl>
            <ButtonGroup>
              <Button
                variant="solidBuilder"
                w="100%"
                mt="2em"
                onClick={handleImportClick}
              >
                Re-Import JSON
              </Button>
              <Button
                variant="solidBuilder"
                w="100%"
                mt="2em"
                isDisabled={videoUrl.length === 0 || errorMessage.length > 0}
                onClick={() => {
                  setAppState(AppStates.FETCH_INFO)
                }}
              >
                Next
              </Button>
            </ButtonGroup>
          </Box>
        </Wrap>
        {/* end of Setup Section  */}
        {/* Info Section */}
        <Wrap
          key="Appstate.INFO"
          display={appState === AppStates.INFO ? 'flex' : 'none'}
          sx={{ ...styles.setup }}
        >
          <Box>
            <Heading>Playalong Builder: Beta</Heading>
            <Text>
              <strong>STEP 2:</strong> Fill in Episode Info
            </Text>
            <FormControl>
              <FormLabel>Video Title</FormLabel>
              <Input
                size={{ base: 'xs', '2xl': 'sm' }}
                type="text"
                value={episodeTitle}
                onChange={(e) => {
                  setEpisodeTitle(e.target.value)
                }}
                onBlur={(e) => {
                  episodeRef.current = {
                    ...episodeRef.current,
                    title: e.target.value
                  }
                }}
              />
              <FormHelperText>Enter the title of the episode</FormHelperText>
            </FormControl>
            <FormControl>
              <FormLabel>Show / Series Name</FormLabel>
              <Input
                size={{ base: 'xs', '2xl': 'sm' }}
                type="text"
                value={seriesName}
                onChange={(e) => {
                  setSeriesName(e.target.value)
                }}
                onBlur={(e) => {
                  episodeRef.current = {
                    ...episodeRef.current,
                    series: {
                      ...episodeRef.current.series,
                      name: e.target.value
                    }
                  }
                }}
              />
              <FormHelperText>Enter the name of the series</FormHelperText>
            </FormControl>
            <FormControl>
              <FormLabel>Creator{"'"}s Name</FormLabel>
              <Input
                size={{ base: 'xs', '2xl': 'sm' }}
                type="text"
                value={creatorName}
                onChange={(e) => {
                  setCreatorName(e.target.value)
                }}
                onBlur={(e) => {
                  episodeRef.current = {
                    ...episodeRef.current,
                    series: {
                      ...episodeRef.current.series,
                      creator: {
                        ...episodeRef.current.series.creator,
                        name: e.target.value
                      }
                    }
                  }
                  saveEpisodeToLocalStorage()
                }}
              />
              <FormHelperText>
                Enter the content creator{"'"}s name
              </FormHelperText>
            </FormControl>
            <FormControl>
              <FormLabel>Creator{"'"}s Learn More Link</FormLabel>
              <Input
                size={{ base: 'xs', '2xl': 'sm' }}
                type="text"
                value={creatorLearnMoreLink}
                onChange={(e) => {
                  setCreatorLearnMoreLink(e.target.value)
                }}
                onBlur={(e) => {
                  episodeRef.current = {
                    ...episodeRef.current,
                    series: {
                      ...episodeRef.current.series,
                      creator: {
                        ...episodeRef.current.series.creator,
                        qrCode: e.target.value
                      }
                    }
                  }
                  saveEpisodeToLocalStorage()
                }}
              />
              <FormHelperText>
                Enter the content creator{"'"}s learn more link (YouTube
                profile, Twitch profile, Linktree, etc.)
              </FormHelperText>
            </FormControl>
            <ButtonGroup justifyContent="space-between">
              <Button
                variant="solidBuilder"
                w="100%"
                mt="2em"
                onClick={() => {
                  setAppState(AppStates.SETUP)
                }}
              >
                Back
              </Button>
              <Button
                variant="solidBuilder"
                w="100%"
                mt="2em"
                isDisabled={episodeRef.current.title.length === 0}
                onClick={() => {
                  setAppState(AppStates.DEV)
                }}
              >
                Next
              </Button>
            </ButtonGroup>
          </Box>
        </Wrap>
        {/* end of Info Section  */}
        {/* Info Section */}
        <Wrap
          key="Appstate.DEV"
          display={appState === AppStates.DEV ? 'flex' : 'none'}
          flexDir="column"
          sx={{ ...styles.dev }}
        >
          <Flex
            flexDir={{ base: 'column-reverse', lg: 'row' }}
            w="100%"
            flexGrow={1}
            alignItems="stretch"
            gap="20px"
            p={{
              base: '5px',
              '2xl': '20px'
            }}
            h="100%"
          >
            <Flex w="100%" flexDir="column" overflow="auto" flexGrow={1}>
              <Tabs
                variant="enclosed"
                onChange={(index) => {
                  setTabIndex(index)
                }}
                sx={{
                  button: {
                    fontWeight: 'bold',
                    fontSize: {
                      base: '10px',
                      '2xl': '14px',
                      '3xl': '16px'
                    },
                    color: 'gray.500',
                    bg: 'yellow.100',
                    '&[aria-selected="true"], &[data-selected]': {
                      bg: 'white',
                      color: 'black',
                      fontWeight: 'bold'
                    }
                  }
                }}
              >
                <TabList>
                  <Tab>Questions & Result Prompts</Tab>
                  <Tab>Music</Tab>
                  <Tab>Video Details</Tab>
                  <Tab>Watch Tutorial</Tab>
                </TabList>
              </Tabs>
              <Box
                flexGrow={5}
                h="100%"
                overflowY="scroll"
                display={tabIndex === 0 ? 'block' : 'none'}
                bg="white"
                pos="relative"
                sx={{
                  svg: {
                    width: '1.5em',
                    height: '1.5em'
                  },
                  '&, .sticky-header, .chakra-accordion__button, .chakra-accordion__item, .chakra-form__label, .chakra-form__helper-text, .chakra-form__error-message, .chakra-form-control input, .chakra-form-control select, .chakra-form-control textarea':
                    {
                      fontSize: { base: '10px', '2xl': '14px', '3xl': '16px' }
                    },
                  '.chakra-accordion__button': {
                    p: '0.5em'
                  }
                }}
              >
                <Flex
                  className="sticky-header"
                  pos="sticky"
                  top="0"
                  zIndex="100"
                  bg="white"
                  p="0.5em 0 0.25em 0.5em"
                  borderBottom="1px solid"
                  borderColor="gray.200"
                >
                  <Flex
                    as="span"
                    flex="1"
                    textAlign="left"
                    gap="1em"
                    zIndex="100"
                    sx={{
                      div: {
                        minW: '4em',
                        fontWeight: 'bold',
                        textAlign: 'center'
                      }
                    }}
                  >
                    <Box>#</Box>
                    <Box>Start</Box>
                    <Box>End</Box>
                    <Box>Prompt</Box>
                  </Flex>
                  <ButtonGroup mr="20px" gap="0.5em" opacity="0.5">
                    <Box>
                      <Trash2 />
                    </Box>
                    <Box>
                      <Copy />
                    </Box>
                    <Box>
                      <GitPullRequestArrow />
                    </Box>
                    <Box>
                      <FastForward />
                    </Box>
                  </ButtonGroup>
                </Flex>
                <Accordion
                  background="white"
                  allowToggle={true}
                  index={selectedEventIndex}
                  onChange={(index: number) => {
                    setSelectedEventIndex(index)
                  }}
                  sx={{
                    '.chakra-accordion__item': {
                      backgroundColor: 'gray.50',
                      '.chakra-accordion__button': {
                        mr: '1em'
                      },
                      '.chakra-accordion__button:hover': {
                        backgroundColor: 'yellow.100',
                        '&[aria-expanded=true], &[data-selected], &[data-selected=true]':
                          {
                            backgroundColor: 'yellow.100'
                          }
                      },
                      '&:nth-of-type(odd)': {
                        backgroundColor: 'gray.200'
                      },
                      '.chakra-accordion__panel': {
                        backgroundColor: 'yellow.50'
                      }
                    }
                  }}
                >
                  <AccordionItem key="start-time" className="header-item">
                    <Flex>
                      <AccordionButton
                        as="div"
                        onClick={(e) => {
                          e.preventDefault()
                        }}
                      >
                        <Flex
                          as="span"
                          flex="1"
                          textAlign="left"
                          gap="1em"
                          sx={{
                            div: {
                              minW: '4em',
                              textAlign: 'center'
                            }
                          }}
                        >
                          <Box></Box>
                          <Box>
                            {new Date(episodeRef.current.startTime * 1000)
                              .toISOString()
                              .slice(11, 19)
                              .replace(/^00:/, '')}
                          </Box>
                          <Box>
                            {new Date(episodeRef.current.startTime * 1000)
                              .toISOString()
                              .slice(11, 19)
                              .replace(/^00:/, '')}
                          </Box>
                          <Box textAlign="left">Episode Start Time</Box>
                        </Flex>
                      </AccordionButton>
                      <ButtonGroup mr="20px" gap="0.5em">
                        <Box
                          as="button"
                          onClick={(e: any) => {
                            e.stopPropagation()
                            seekToTime(episodeRef.current.startTime)
                          }}
                          aria-label="Seek To Episode Start Time"
                        >
                          <FastForward />
                        </Box>
                      </ButtonGroup>
                    </Flex>
                  </AccordionItem>
                  {eventList}
                </Accordion>
              </Box>
              {/* end events */}

              {/* start music */}
              <Box
                flexGrow={5}
                h="100%"
                bg="white"
                overflowY="scroll"
                display={tabIndex === 1 ? 'block' : 'none'}
                p={4}
              >
                <FormControl>
                  <FormLabel>
                    Intro Music Source{' '}
                    <Tooltip label="Music that plays before the episode starts, either from the current video or different a YouTube video">
                      <Icon as={Info} />
                    </Tooltip>
                  </FormLabel>
                  <Select
                    size={{ base: 'xs', '2xl': 'sm' }}
                    value={introMusic.source}
                    onChange={(e) => {
                      const newSource = {
                        ...introMusic,
                        source: e.target.value as
                          | 'mp3'
                          | 'youtube'
                          | 'current'
                          | 'none'
                      }
                      setIntroMusic(newSource)
                      episodeRef.current.introMusic = newSource
                      saveEpisodeToLocalStorage()
                    }}
                  >
                    <option value="none">No Music</option>
                    <option value="current">Current YouTube Video</option>
                    <option value="youtube">Other YouTube Video</option>
                    {/* <option value="mp3">MP3</option> */}
                  </Select>
                </FormControl>
                <FormControl
                  display={introMusic.source === 'mp3' ? 'block' : 'none'}
                >
                  <FormLabel>URL to MP3</FormLabel>
                  <Input
                    size={{ base: 'xs', '2xl': 'sm' }}
                    type="text"
                    value={introMusic.url}
                    onChange={(e) => {
                      setIntroMusic({
                        ...introMusic,
                        url: e.target.value
                      })
                    }}
                    onBlur={() => {
                      saveMusic()
                    }}
                  />
                </FormControl>
                <FormControl
                  display={introMusic.source === 'youtube' ? 'block' : 'none'}
                >
                  <FormLabel>YouTube Video URL</FormLabel>
                  <Input
                    size={{ base: 'xs', '2xl': 'sm' }}
                    type="text"
                    value={introMusic.url}
                    onChange={(e) => {
                      setIntroMusic({
                        ...introMusic,
                        url: e.target.value
                      })
                    }}
                    onBlur={() => {
                      saveMusic()
                    }}
                  />
                </FormControl>
                <Grid templateColumns="1fr 1fr" gap="1em">
                  <FormControl
                    display={introMusic.source !== 'none' ? 'block' : 'none'}
                    isInvalid={introMusic.startTime > introMusic.endTime}
                  >
                    <FormLabel>
                      Intro Music Start Time{' '}
                      <Tooltip label="The time in seconds where the intro music starts playing in the video">
                        <Icon as={Info} />
                      </Tooltip>
                    </FormLabel>
                    <Input
                      size={{ base: 'xs', '2xl': 'sm' }}
                      type="number"
                      min={0}
                      value={introMusic.startTime}
                      onChange={(e) => {
                        setIntroMusic({
                          ...introMusic,
                          startTime: parseInt(e.target.value)
                        })
                      }}
                      onBlur={() => {
                        saveMusic()
                      }}
                    />
                  </FormControl>
                  <FormControl
                    display={introMusic.source !== 'none' ? 'block' : 'none'}
                    isInvalid={introMusic.endTime < introMusic.startTime}
                  >
                    <FormLabel>
                      Intro Music End Time{' '}
                      <Tooltip label="The time in seconds where the intro music stops and loops back to the start">
                        <Icon as={Info} />
                      </Tooltip>
                    </FormLabel>
                    <Input
                      size={{ base: 'xs', '2xl': 'sm' }}
                      type="number"
                      value={introMusic.endTime}
                      onChange={(e) => {
                        setIntroMusic({
                          ...introMusic,
                          endTime: parseInt(e.target.value)
                        })
                      }}
                      onBlur={() => {
                        saveMusic()
                      }}
                    />
                  </FormControl>
                </Grid>

                <FormControl>
                  <FormLabel>
                    Guessing Music Source{' '}
                    <Tooltip label="Music that plays while the video is paused and the players are guessing, either from the current video or different a YouTube video">
                      <Icon as={Info} />
                    </Tooltip>
                  </FormLabel>
                  <Select
                    size={{ base: 'xs', '2xl': 'sm' }}
                    value={promptMusic.source}
                    onChange={(e) => {
                      const newSource = {
                        ...promptMusic,
                        source: e.target.value as
                          | 'mp3'
                          | 'youtube'
                          | 'current'
                          | 'none'
                      }
                      setPromptMusic(newSource)
                      episodeRef.current.promptMusic = newSource
                      saveEpisodeToLocalStorage()
                    }}
                  >
                    <option value="none">No Music</option>
                    <option value="current">Current YouTube Video</option>
                    <option value="youtube">Other YouTube Video</option>
                    {/* <option value="mp3">MP3 Link</option> */}
                  </Select>
                </FormControl>
                <FormControl
                  display={promptMusic.source === 'mp3' ? 'block' : 'none'}
                >
                  <FormLabel>MP3 Link</FormLabel>
                  <Input
                    size={{ base: 'xs', '2xl': 'sm' }}
                    type="text"
                    value={promptMusic.url}
                    onChange={(e) => {
                      setPromptMusic({
                        ...promptMusic,
                        url: e.target.value
                      })
                    }}
                    onBlur={() => {
                      saveMusic()
                    }}
                  />
                </FormControl>
                <FormControl
                  display={promptMusic.source === 'youtube' ? 'block' : 'none'}
                  isInvalid={
                    promptMusic?.url === undefined &&
                    getYouTubeVideoId(promptMusic?.url ?? '') !== null
                  }
                >
                  <FormLabel>YouTube Video URL</FormLabel>
                  <Input
                    size={{ base: 'xs', '2xl': 'sm' }}
                    type="text"
                    value={promptMusic.url}
                    onChange={(e) => {
                      setPromptMusic({
                        ...promptMusic,
                        url: e.target.value
                      })
                    }}
                    onBlur={() => {
                      saveMusic()
                    }}
                  />
                </FormControl>
                <Grid templateColumns="1fr 1fr" gap="1em">
                  <FormControl
                    display={promptMusic.source !== 'none' ? 'block' : 'none'}
                    isInvalid={promptMusic.startTime >= promptMusic.endTime}
                  >
                    <FormLabel>
                      Guessing Music Start Time{' '}
                      <Tooltip label="The time in seconds where the guessing music starts playing in the video">
                        <Icon as={Info} />
                      </Tooltip>
                    </FormLabel>
                    <Input
                      size={{ base: 'xs', '2xl': 'sm' }}
                      type="number"
                      min={0}
                      max={promptMusic.endTime - 1}
                      value={promptMusic.startTime}
                      onChange={(e) => {
                        setPromptMusic({
                          ...promptMusic,
                          startTime: parseInt(e.target.value)
                        })
                      }}
                      onBlur={() => {
                        saveMusic()
                      }}
                    />
                  </FormControl>
                  <FormControl
                    display={promptMusic.source !== 'none' ? 'block' : 'none'}
                    isInvalid={promptMusic.endTime < promptMusic.startTime}
                  >
                    <FormLabel>
                      Guessing Music End Time{' '}
                      <Tooltip label="The time in seconds where the guessing music stops playing in the video">
                        <Icon as={Info} />
                      </Tooltip>
                    </FormLabel>
                    <Input
                      size={{ base: 'xs', '2xl': 'sm' }}
                      type="number"
                      min={promptMusic.startTime}
                      value={promptMusic.endTime}
                      onChange={(e) => {
                        setPromptMusic({
                          ...promptMusic,
                          endTime: parseInt(e.target.value)
                        })
                      }}
                      onBlur={() => {
                        saveMusic()
                      }}
                    />
                  </FormControl>
                </Grid>
              </Box>
              {/* end music */}

              {/* start episode details */}
              <Box
                flexGrow={5}
                h="100%"
                bg="white"
                overflowY="scroll"
                display={tabIndex === 2 ? 'block' : 'none'}
                p={4}
              >
                <FormControl>
                  <FormLabel>Title</FormLabel>
                  <Input
                    size={{ base: 'xs', '2xl': 'sm' }}
                    type="text"
                    value={episodeTitle}
                    onChange={(e) => {
                      setEpisodeTitle(e.target.value)
                    }}
                    onBlur={(e) => {
                      episodeRef.current = {
                        ...episodeRef.current,
                        title: e.target.value
                      }
                      saveEpisodeToLocalStorage()
                    }}
                  />
                  <FormHelperText>
                    Enter the title of the episode
                  </FormHelperText>
                </FormControl>
                <FormControl>
                  <FormLabel>Show / Series Name</FormLabel>
                  <Input
                    size={{ base: 'xs', '2xl': 'sm' }}
                    type="text"
                    value={seriesName}
                    onChange={(e) => {
                      setSeriesName(e.target.value)
                    }}
                    onBlur={(e) => {
                      episodeRef.current = {
                        ...episodeRef.current,
                        series: {
                          ...episodeRef.current.series,
                          name: e.target.value
                        }
                      }
                      saveEpisodeToLocalStorage()
                    }}
                  />
                  <FormHelperText>
                    (Used to identify the series vs the creator / youtube
                    channel, i.e. {'"'}Taskmaster{'"'})
                  </FormHelperText>
                </FormControl>
                <FormControl>
                  <FormLabel>Creator{"'"}s Name</FormLabel>
                  <Input
                    size={{ base: 'xs', '2xl': 'sm' }}
                    type="text"
                    value={creatorName}
                    onChange={(e) => {
                      setCreatorName(e.target.value)
                    }}
                    onBlur={(e) => {
                      episodeRef.current = {
                        ...episodeRef.current,
                        series: {
                          ...episodeRef.current.series,
                          creator: {
                            ...episodeRef.current.series.creator,
                            name: e.target.value
                          }
                        }
                      }
                      saveEpisodeToLocalStorage()
                    }}
                  />
                  <FormHelperText>
                    Enter the content creator{"'"}s name (i.e. {'"'}Alex Horne
                    {'"'})
                  </FormHelperText>
                </FormControl>
                <FormControl>
                  <FormLabel>Creator{"'"}s Learn More Link</FormLabel>
                  <Input
                    size={{ base: 'xs', '2xl': 'sm' }}
                    type="text"
                    value={creatorLearnMoreLink}
                    onChange={(e) => {
                      setCreatorLearnMoreLink(e.target.value)
                    }}
                    onBlur={(e) => {
                      episodeRef.current = {
                        ...episodeRef.current,
                        series: {
                          ...episodeRef.current.series,
                          creator: {
                            ...episodeRef.current.series.creator,
                            qrCode: e.target.value
                          }
                        }
                      }
                      saveEpisodeToLocalStorage()
                    }}
                  />
                  <FormHelperText>
                    Enter the content creator{"'"}s learn more link (YouTube
                    profile, Twitch profile, Linktree, etc.)
                  </FormHelperText>
                </FormControl>
              </Box>
              {/* end episode details */}
              {/* start tutorial */}
              <Box
                flexGrow={5}
                h="100%"
                bg="white"
                overflowY="scroll"
                display={tabIndex === 3 ? 'block' : 'none'}
                p={4}
              >
                <Box
                  as={ReactPlayer}
                  url="https://www.youtube.com/watch?v=zAsH3DYweCs"
                  width="100%"
                  height="auto"
                  aspectRatio={16 / 9}
                  controls={true}
                  objectFit={'cover'}
                  sx={{
                    width: '100% !important',
                    height: 'auto !important',
                    aspectRatio: '16 / 9'
                  }}
                />
              </Box>
              {/* end tutoial */}

              <Modal
                isOpen={toDeletePrompt !== undefined}
                onClose={() => {
                  setToDelete(undefined)
                }}
              >
                {toDelete !== undefined && toDeletePrompt !== undefined && (
                  <>
                    <ModalOverlay />
                    <ModalContent>
                      <ModalHeader>
                        Remove{' '}
                        {toDeletePrompt.type === 'results'
                          ? 'Results'
                          : 'Prompt'}
                      </ModalHeader>
                      <ModalCloseButton />
                      <ModalBody>
                        {toDeleteResulstFor !== undefined ? (
                          <>
                            Are you sure you want to remove the results for the
                            prompt &quot;{toDeleteResulstFor.question}&quot;?
                          </>
                        ) : (
                          <>
                            Are you sure you want to remove the prompt &quot;
                            {toDeletePrompt.question}&quot;?
                            <br />
                            (Deleting this prompt will also remove any results
                            associated with it)
                          </>
                        )}
                      </ModalBody>

                      <ModalFooter>
                        <Button
                          colorScheme="red"
                          onClick={() => {
                            removePrompt(toDelete)
                            setToDelete(undefined)
                          }}
                        >
                          Remove
                        </Button>
                        <Button
                          colorScheme="blue"
                          mr={3}
                          onClick={() => {
                            setToDelete(undefined)
                          }}
                        >
                          Close
                        </Button>
                      </ModalFooter>
                    </ModalContent>
                  </>
                )}
              </Modal>

              <ButtonGroup>
                <Button
                  variant="solidBuilder"
                  w="100%"
                  mt="2em"
                  onClick={() => {
                    setAppState(AppStates.INFO)
                  }}
                >
                  Back
                </Button>
                <Button
                  variant="solidBuilder"
                  w="100%"
                  mt="2em"
                  onClick={handleImportClick}
                >
                  Import
                </Button>
                <input
                  type="file"
                  accept=".json"
                  onChange={handleImportFileChange}
                  ref={fileInputRef}
                  style={{ display: 'none' }} // Hide the input
                />
                <Button
                  variant="solidBuilder"
                  w="100%"
                  mt="2em"
                  onClick={() => {
                    exportEpisodeData()
                  }}
                >
                  Export
                </Button>
                <Button
                  variant="solidBuilder"
                  w="100%"
                  mt="2em"
                  onClick={() => {
                    setShowResetModal(true)
                  }}
                >
                  Start Over
                </Button>
                <Button
                  variant="solidBuilder"
                  w="100%"
                  mt="2em"
                  onClick={() => {
                    playDebugEpisode()
                  }}
                >
                  Play Episode
                </Button>
                <Modal
                  isOpen={showResetModal}
                  onClose={() => {
                    setShowResetModal(false)
                  }}
                >
                  <ModalOverlay />
                  <ModalContent>
                    <ModalHeader>Restart</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                      Are You Sure You Want To Reset your progress?
                      <br />
                      (You cannot recover it)
                    </ModalBody>

                    <ModalFooter>
                      <Button
                        colorScheme="red"
                        onClick={() => {
                          resetEpisode()
                          setShowResetModal(false)
                        }}
                      >
                        Restart
                      </Button>
                      <Button
                        colorScheme="blue"
                        mr={3}
                        onClick={() => {
                          setShowResetModal(false)
                        }}
                      >
                        Close
                      </Button>
                    </ModalFooter>
                  </ModalContent>
                </Modal>
              </ButtonGroup>
            </Flex>
            <Flex
              direction={{
                base: 'column',
                md: 'row',
                lg: 'column'
              }}
              gap={{
                base: '0',
                md: '20px',
                lg: '0'
              }}
              justifyContent="center"
              alignItems="center"
            >
              <Box>
                <Heading fontSize={{ base: '16px', '2xl': '56px' }}>
                  Playalong Builder: Beta
                </Heading>
                <Text fontSize={{ base: '10px', '2xl': '14px', '3xl': '16px' }}>
                  <strong>STEP 3:</strong> Add Your Prompts
                </Text>
              </Box>
              <Box
                h="65vh"
                pb="50px"
                maxH={{
                  base: 'max(30vh, 28.125vw)',
                  '2xl': '28.125vw'
                }}
                maxW={{
                  base: 'calc(max(30vh, 28.125vw) * 1.7777777777777777)',
                  // '2xl': '25vw',
                  '2xl': '50vw'
                }}
                aspectRatio={16 / 9}
              >
                <ReactPlayer
                  ref={videoRef}
                  url={episodeRef.current.videoUrl}
                  width="100%"
                  height="100%"
                  overflow="hidden"
                  style={{
                    borderRadius: '20px'
                  }}
                  onSeek={() => {
                    forceRefresh()
                  }}
                  onPlay={() => {
                    forceRefresh()
                  }}
                  onPause={() => {
                    forceRefresh()
                  }}
                  config={{
                    youtube: {
                      playerVars: {
                        controls: 1,
                        disablekb: 1,
                        cc_load_policy: 1,
                        fs: 0,
                        rel: 0,
                        showinfo: 0,
                        widget_referrer: 'https://www.reactvts.com'
                      }
                    },
                    twitch: {
                      options: {
                        controls: 1
                      }
                    }
                  }}
                />
                <Flex>
                  <ButtonGroup w="100%" justifyContent="center">
                    <Button
                      h="2.5rem"
                      variant="solidBuilder"
                      onClick={() => {
                        videoSeekTo(
                          Math.ceil(videoRef.current?.getCurrentTime() ?? 0) - 5
                        )
                        forceRefreshForButtons()
                      }}
                    >
                      <ChevronLeft /> 5s
                    </Button>
                    <Button
                      h="2.5rem"
                      variant="solidBuilder"
                      onClick={() => {
                        videoSeekTo(
                          Math.ceil(videoRef.current?.getCurrentTime() ?? 0) - 1
                        )
                        forceRefreshForButtons()
                      }}
                    >
                      <ChevronLeft /> 1s
                    </Button>
                    {tabIndex === 0 && (
                      <>
                        {selectedEventIndex === -1 && (
                          <Flex h="2.5em">
                            <Select
                              h="2.5rem"
                              backgroundColor="gray.100"
                              maxW="12em"
                              value={addPromptType}
                              onChange={(e) => {
                                setAddPromptType(e.target.value)
                              }}
                              colorScheme="yellow"
                              borderTopRightRadius={0}
                              borderBottomRightRadius={0}
                              border="2px solid"
                              borderRight="none"
                              borderColor="gray.700"
                            >
                              {questionTypeOptions}
                            </Select>
                            <NumberInput
                              h="2.5em"
                              size={{ base: 'xs', '2xl': 'sm' }}
                              backgroundColor="gray.100"
                              maxW="13em"
                              min={1}
                              step={5}
                              max={1000}
                              border="0px solid"
                              borderColor="gray.700"
                              borderLeft="1px solid"
                              borderLeftColor="gray.300"
                              borderRight="none"
                              value={addPromptPoints}
                              borderRadius="0"
                              onChange={(value) => {
                                if (value === '') {
                                  setAddPromptPoints(undefined)
                                } else {
                                  setAddPromptPoints(
                                    Math.min(1000, Math.round(parseInt(value)))
                                  )
                                }
                              }}
                              onBlur={(e) => {
                                const value = e.target.value
                                if (value === '') {
                                  setAddPromptPoints(1)
                                } else {
                                  setAddPromptPoints(
                                    Math.round(parseInt(value))
                                  )
                                }
                              }}
                              sx={{
                                '& > input': {
                                  height: '100%',
                                  backgroundColor: 'gray.100',
                                  borderRadius: '0',
                                  borderTop: '2px solid',
                                  borderBottom: '2px solid',
                                  textAlign: 'right',
                                  maxWidth: '9em',
                                  minWidth: '9em',
                                  pr: '5.5em'
                                },
                                '&:after': {
                                  content: '"Points"',
                                  fontSize: '0.75em',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  fontWeight: '800',
                                  position: 'absolute',
                                  right: '2em',
                                  top: '0em',
                                  bottom: '0em',
                                  margin: 'auto',
                                  zIndex: '1000'
                                }
                              }}
                            >
                              <NumberInputField />

                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                            <Button
                              h="2.5rem"
                              borderTopLeftRadius={0}
                              borderBottomLeftRadius={0}
                              variant="solidBuilder"
                              borderLeft="none"
                              onClick={() => {
                                addPrompt()
                              }}
                            >
                              <Box as={CirclePlus} />
                            </Button>
                          </Flex>
                        )}
                        {selectedEventIndex === 0 && (
                          <>
                            <Button
                              h="2.5rem"
                              variant="solidBuilder"
                              onClick={() => {
                                setEpisodeStartTime()
                              }}
                            >
                              <Box as={ClockArrowDown} mr="0.75em" />
                              Start
                            </Button>
                          </>
                        )}
                        {selectedEventIndex >= 1 && (
                          <>
                            {selectedPrompt.resultsFor !== undefined &&
                              episodeRef.current.events.find(
                                (event) =>
                                  event.id === selectedPrompt.resultsFor
                              )?.type !== 'tieredAnswers' && (
                                <Button
                                  h="2.5rem"
                                  variant="solidBuilder"
                                  isDisabled={disableSetResultStartTime}
                                  onClick={() => {
                                    setPromptStartTime()
                                  }}
                                >
                                  <Tooltip label="Set Result Start Time">
                                    <Box as={ClockArrowDown} />
                                  </Tooltip>
                                </Button>
                              )}
                            {selectedPrompt.type === 'finalResults' && (
                              <Button
                                h="2.5rem"
                                variant="solidBuilder"
                                isDisabled={disableFinalResultsStartTime}
                                onClick={() => {
                                  setPromptStartTime()
                                }}
                              >
                                <Tooltip label="Set Result Start Time">
                                  <Box as={ClockArrowDown} />
                                </Tooltip>
                              </Button>
                            )}
                            {selectedPrompt.resultsFor === undefined &&
                              selectedPrompt.type !== 'finalResults' && (
                                <>
                                  <Button
                                    h="2.5rem"
                                    variant="solidBuilder"
                                    isDisabled={disableSetPromptStartTime}
                                    onClick={() => {
                                      setPromptStartTime()
                                    }}
                                  >
                                    <Tooltip label="Set Guessing Start Time">
                                      <Box as={ClockArrowDown} />
                                    </Tooltip>
                                  </Button>
                                  <Button
                                    h="2.5rem"
                                    isDisabled={disableSetPromptEndTime}
                                    variant="solidBuilder"
                                    onClick={() => {
                                      setPromptEndTime()
                                    }}
                                  >
                                    <Tooltip label="Set Guessing End Time">
                                      <Box as={ClockArrowUp} />
                                    </Tooltip>
                                  </Button>
                                  <Button
                                    h="2.5rem"
                                    isDisabled={
                                      selectedPrompt.endTime === undefined
                                    }
                                    variant="solidBuilder"
                                    onClick={() => {
                                      clearPromptEndTime()
                                    }}
                                  >
                                    <Tooltip label="Clear Guessing End Time (Will Autopause instead)">
                                      <Box as={CircleX} />
                                    </Tooltip>
                                  </Button>
                                  <Button
                                    h="2.5rem"
                                    isDisabled={
                                      selectedPrompt.resultsFor !== undefined
                                    }
                                    variant="solidBuilder"
                                    onClick={() => {
                                      duplicatePrompt(selectedPrompt.id, 'at')
                                    }}
                                  >
                                    <Tooltip label="Duplicate selected Prompt at current video time">
                                      <Box as={Copy} />
                                    </Tooltip>
                                  </Button>
                                </>
                              )}
                          </>
                        )}
                      </>
                    )}
                    <Button
                      h="2.5rem"
                      variant="solidBuilder"
                      onClick={() => {
                        videoSeekTo(
                          Math.ceil(videoRef.current?.getCurrentTime() ?? 0) + 1
                        )
                        forceRefreshForButtons()
                      }}
                    >
                      1s <ChevronRight />
                    </Button>
                    <Button
                      h="2.5rem"
                      variant="solidBuilder"
                      onClick={() => {
                        videoSeekTo(
                          Math.ceil(videoRef.current?.getCurrentTime() ?? 0) + 5
                        )
                        forceRefreshForButtons()
                      }}
                    >
                      5s <ChevronRight />
                    </Button>
                  </ButtonGroup>
                </Flex>
              </Box>
            </Flex>
          </Flex>
        </Wrap>
        {/* end of Info Section  */}
      </AnimatePresence>
    </Box>
  )
}

export { Page }

interface WrapProps extends CenterProps {}

const EventPanel = React.memo(
  ({
    event,
    episodeRef,
    index,
    setEventType,
    addNewTieredAnswer,
    removeTieredAnswer,
    saveEpisodeToLocalStorage
  }: {
    event: Event
    index: number
    episodeRef: React.MutableRefObject<Episode>
    setEventType: (eventType: string) => void
    addNewTieredAnswer: (index: number) => void
    removeTieredAnswer: (index: number, tIndex: number) => void
    saveEpisodeToLocalStorage: (text?: string) => void
  }) => {
    return (
      <AccordionPanel pb={4}>
        <Box>
          <FormControl>
            <FormLabel>Question</FormLabel>
            <Input
              size={{ base: 'xs', '2xl': 'sm' }}
              type="text"
              defaultValue={event.question as string}
              placeholder={PLACEHOLDER_QUESTION}
              onBlur={(e) => {
                episodeRef.current.events[index].question = e.target.value
                saveEpisodeToLocalStorage()
              }}
            />
            <FormHelperText>Enter the question for the prompt</FormHelperText>
          </FormControl>
          <FormControl>
            <FormLabel>Display Answer (optional)</FormLabel>
            <Input
              size={{ base: 'xs', '2xl': 'sm' }}
              type="text"
              placeholder=""
              defaultValue={event.displayAnswer}
              onBlur={(e) => {
                episodeRef.current.events[index].displayAnswer = e.target.value
                saveEpisodeToLocalStorage()
              }}
            />
            <FormHelperText>
              Used to elaborate on the correct answer, can be left blank
            </FormHelperText>
          </FormControl>
          <Flex gap="1em">
            <FormControl>
              <FormLabel>
                Question Type{' '}
                <Tooltip
                  label={
                    <Box py="5px">
                      <Text color="white" fontSize="12px">
                        <strong>Text, Number, Percentage </strong> - A simple
                        textbox prompt
                      </Text>
                      <Text color="white" fontSize="12px">
                        <strong>Multiple Choice</strong> - Players pick one
                        choice from multiple options
                      </Text>
                      <Text color="white" fontSize="12px">
                        <strong>Order</strong> - User types in the order of the
                        listed options (i.e. 53421)
                      </Text>
                      <Text color="white" fontSize="12px" mb="0">
                        <strong>Tiered Answers</strong> - {"'"}Family Feud{"'"}{' '}
                        style scoring, user makes one guess and scores points
                        based the tiered answers
                      </Text>
                    </Box>
                  }
                  // label="Select the type of question for the prompt"
                >
                  <Icon as={Info} />
                </Tooltip>
              </FormLabel>

              <Select
                size={{ base: 'xs', '2xl': 'sm' }}
                value={episodeRef.current.events[index].type}
                onChange={(e) => {
                  setEventType(e.target.value)
                  saveEpisodeToLocalStorage()
                }}
              >
                {questionTypeOptions}
              </Select>
            </FormControl>
            {episodeRef.current.events[index].type !== 'tieredAnswers' && (
              <FormControl>
                <FormLabel>Points</FormLabel>
                <NumberInput
                  size={{ base: 'xs', '2xl': 'sm' }}
                  defaultValue={episodeRef.current.events[index].points}
                  step={1}
                  min={1}
                  max={1000}
                  onBlur={(e) => {
                    episodeRef.current.events[index].points = parseInt(
                      e.target.value
                    )
                    saveEpisodeToLocalStorage()
                  }}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
            )}
          </Flex>
          {/* string compare options */}
          <Box
            display={
              episodeRef.current.events[index].type === 'stringCompare'
                ? 'grid'
                : 'none'
            }
            gridTemplateColumns="1fr 1fr"
            gap="1em"
          >
            <FormControl
              sx={{
                gridColumn: 'span 2'
              }}
            >
              <FormLabel>
                Acceptable Answers{' '}
                <Tooltip label="Allows for multiple answers to be accepted, such as regional spelling or similar answers (i.e. 'Car' and 'Automobile')">
                  <Icon as={Info} />
                </Tooltip>
              </FormLabel>
              <MultiInput
                value={
                  (episodeRef.current.events[index]
                    .acceptableAnswers as string[]) ?? []
                }
                onChange={(value) => {
                  episodeRef.current.events[index].acceptableAnswers = value
                  saveEpisodeToLocalStorage()
                }}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Fuzzy Compare Percentage</FormLabel>
              <NumberInput
                size={{ base: 'xs', '2xl': 'sm' }}
                defaultValue={episodeRef.current.events[
                  index
                ].threshold?.toFixed(2)}
                step={0.05}
                min={0.5}
                max={1}
                // onChange={(e) => {
                //   episodeRef.current.events[index].threshold = parseFloat(e)
                //   refresh
                // }}
                onBlur={(e) => {
                  episodeRef.current.events[index].threshold = parseFloat(
                    e.target.value
                  )
                  saveEpisodeToLocalStorage()
                }}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <FormHelperText>
                How acurate must the guess be? (between 0.5 and 1, higher being
                more accurate)
              </FormHelperText>
            </FormControl>
            {/* <FormControl>
              <FormLabel>Partial Score Step Amount </FormLabel>
              <NumberInput
                size={{ base: 'xs', '2xl': 'sm' }}
                defaultValue={episodeRef.current.events[index].stepAmount}
                step={0.05}
                min={0.5}
                max={1}
                onBlur={(e) => {
                  episodeRef.current.events[index].stepAmount = parseFloat(
                    e.target.value
                  )
                  saveEpisodeToLocalStorage()
                }}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <FormHelperText>
                The percantage amount between each level of
                &quot;closeness&quot; to the correct guess
              </FormHelperText>
            </FormControl>
            <FormControl>
              <FormLabel>Score Drop per Step</FormLabel>
              <NumberInput
                size={{ base: 'xs', '2xl': 'sm' }}
                defaultValue={episodeRef.current.events[index].stepDrop}
                step={1}
                min={1}
                max={1000}
                onBlur={(e) => {
                  episodeRef.current.events[index].stepDrop = parseInt(
                    e.target.value
                  )
                  saveEpisodeToLocalStorage()
                }}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <FormHelperText>
                The number of points to drop the possible score based on how
                many &quot;steps&quot; away from the correct guess
              </FormHelperText>
            </FormControl> */}
          </Box>
          {/* multiStringCompare options */}
          <Box
            display={
              episodeRef.current.events[index].type === 'tieredAnswers'
                ? 'grid'
                : 'none'
            }
            gridTemplateColumns="1fr"
            gap="1em"
          >
            <FormControl>
              <FormLabel>
                Tiered Answers{' '}
                <Button
                  size="xs"
                  onClick={(e) => {
                    e.stopPropagation()
                    addNewTieredAnswer(index)
                  }}
                >
                  Add
                </Button>
              </FormLabel>

              <Accordion
                className="multi"
                allowToggle
                sx={{
                  '&.multi .chakra-accordion__item': {
                    backgroundColor: 'gray.900',
                    '.chakra-accordion__button': {
                      color: 'white',
                      mr: '1em',
                      svg: {
                        color: 'white'
                      }
                    },
                    '.chakra-accordion__button:hover': {
                      backgroundColor: 'gray.700',
                      '&[aria-expanded=true], &[data-selected], &[data-selected=true]':
                        {
                          backgroundColor: 'yellow.900'
                        }
                    },
                    '&:nth-of-type(odd)': {
                      backgroundColor: 'gray.800'
                    },
                    '.chakra-accordion__panel': {
                      backgroundColor: 'gray.100'
                    }
                  }
                }}
              >
                {episodeRef.current.events[index].tieredAnswers?.map(
                  (tieredAnswer: TieredAnswer, tIndex: number) => {
                    return (
                      <AccordionItem key={tIndex}>
                        <Flex>
                          <AccordionButton>
                            <AccordionIcon />
                            {tieredAnswer?.acceptableAnswers?.[0] ??
                              '(Add Acceptable Answers)'}{' '}
                            | {tieredAnswer.points} Points
                          </AccordionButton>
                          <Button
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation()
                              removeTieredAnswer(index, tIndex)
                            }}
                          >
                            Remove
                          </Button>
                        </Flex>
                        <AccordionPanel>
                          <FormControl>
                            <FormLabel>Acceptable Answers</FormLabel>
                            <MultiInput
                              value={
                                (tieredAnswer.acceptableAnswers as string[]) ??
                                []
                              }
                              onChange={(value) => {
                                if (
                                  episodeRef.current.events[index]
                                    .tieredAnswers === undefined
                                ) {
                                  return
                                }

                                episodeRef.current.events[index].tieredAnswers[
                                  tIndex
                                ].acceptableAnswers = value

                                saveEpisodeToLocalStorage()
                              }}
                            />
                            <FormHelperText>
                              Enter the acceptable answers for the prompt
                            </FormHelperText>
                          </FormControl>
                          <FormControl>
                            <FormLabel>Points</FormLabel>
                            <NumberInput
                              size={{ base: 'xs', '2xl': 'sm' }}
                              defaultValue={
                                episodeRef.current.events[index]
                                  .tieredAnswers?.[tIndex]?.points
                              }
                              step={1}
                              min={1}
                              max={1000}
                              onBlur={(e) => {
                                if (
                                  episodeRef.current.events[index]
                                    .tieredAnswers?.[tIndex]?.points ===
                                  undefined
                                ) {
                                  return
                                }
                                episodeRef.current.events[index].tieredAnswers[
                                  tIndex
                                ].points = parseInt(e.target.value)

                                saveEpisodeToLocalStorage()
                              }}
                            >
                              <NumberInputField />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                          </FormControl>
                        </AccordionPanel>
                      </AccordionItem>
                    )
                  }
                )}
              </Accordion>
            </FormControl>

            <FormControl>
              <FormLabel>Fuzzy Compare Percentage</FormLabel>
              <NumberInput
                size={{ base: 'xs', '2xl': 'sm' }}
                defaultValue={0.5}
                value={(event.threshold ?? 0.8).toFixed(2)}
                step={0.05}
                min={0.5}
                max={1}
                onBlur={(e) => {
                  episodeRef.current.events[index].threshold = parseFloat(
                    e.target.value
                  )

                  saveEpisodeToLocalStorage()
                }}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <FormHelperText>
                How acurate must the guess be? (between 0.5 and 1, higher being
                more accurate)
              </FormHelperText>
            </FormControl>
          </Box>
          {/* order options */}
          {episodeRef.current.events[index].type === 'order' && (
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap="1em">
              <FormControl
                sx={{
                  gridColumn: 'span 2'
                }}
              >
                <FormLabel>
                  Enter Choices for &quot;Order&quot; Question
                </FormLabel>
                <MultiInput
                  showNumbers={true}
                  value={episodeRef.current.events[index].choices ?? []}
                  onChange={(value) => {
                    episodeRef.current.events[index].choices = value

                    saveEpisodeToLocalStorage()
                  }}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Order Answer i.e 12345</FormLabel>
                <Input
                  size={{ base: 'xs', '2xl': 'sm' }}
                  type="number"
                  defaultValue={episodeRef.current.events[index].answer ?? ''}
                  onBlur={(e) => {
                    episodeRef.current.events[index].answer = parseInt(
                      e.target.value
                    ).toString()
                  }}
                />
                <FormHelperText>
                  Enter the order as numeric value
                </FormHelperText>
              </FormControl>
            </Box>
          )}
          {/* multiple choice options */}
          {episodeRef.current.events[index].type === 'multiple' && (
            <Box display="grid" gridTemplateColumns="1fr" gap="5px">
              <FormControl>
                <FormLabel>Enter Multiple Choice options</FormLabel>
                <MultiInput
                  value={episodeRef.current.events[index].choices ?? []}
                  onChange={(value) => {
                    episodeRef.current.events[index].choices = value

                    saveEpisodeToLocalStorage()
                  }}
                />
              </FormControl>

              <FormControl isDisabled={(event.choices ?? []).length === 0}>
                <FormLabel>Select Correct answer</FormLabel>
                <Select
                  size={{ base: 'xs', '2xl': 'sm' }}
                  value={event.answer ?? ''}
                  onChange={(e) => {
                    episodeRef.current.events[index].answer = parseInt(
                      e.target.value
                    )
                    saveEpisodeToLocalStorage()
                  }}
                >
                  <option value="" disabled>
                    Select Correct Answer
                  </option>
                  {(event.choices ?? []).map((choice, index) => {
                    return (
                      <option key={index} value={index}>
                        {choice}
                      </option>
                    )
                  })}
                </Select>
                {/* <FormHelperText>index</FormHelperText> */}
              </FormControl>
            </Box>
          )}
          {/* percentage Curve options */}
          {episodeRef.current.events[index].type === 'percentageCurve' && (
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap="1em">
              <FormControl>
                <FormLabel>Answer</FormLabel>
                <NumberInput
                  size={{ base: 'xs', '2xl': 'sm' }}
                  defaultValue={episodeRef.current.events[index].answer}
                  step={0.01}
                  min={0.0}
                  max={1}
                  onBlur={(e) => {
                    episodeRef.current.events[index].answer = parseFloat(
                      e.target.value
                    )
                  }}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormHelperText>
                  Enter percentage as decimal value between 0 and 1
                </FormHelperText>
              </FormControl>
              <FormControl>
                <FormLabel>Percentage threshold... Percentage</FormLabel>
                <NumberInput
                  size={{ base: 'xs', '2xl': 'sm' }}
                  defaultValue={episodeRef.current.events[index].threshold}
                  step={0.01}
                  min={0.01}
                  max={1}
                  onBlur={(e) => {
                    episodeRef.current.events[index].threshold = parseFloat(
                      e.target.value
                    )
                  }}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormHelperText>
                  How acurate must the guess be? (between 0.5 and 1, higher
                  being more accurate)
                </FormHelperText>
              </FormControl>
              <FormControl>
                <FormLabel>Partial Score Step Amount </FormLabel>
                <NumberInput
                  size={{ base: 'xs', '2xl': 'sm' }}
                  defaultValue={episodeRef.current.events[index].stepAmount}
                  step={0.05}
                  min={0.1}
                  max={1}
                  onBlur={(e) => {
                    episodeRef.current.events[index].stepAmount = parseFloat(
                      e.target.value
                    )
                  }}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormHelperText>
                  The percantage amount between each level of
                  &quot;closeness&quot; to the correct guess
                </FormHelperText>
              </FormControl>
              <FormControl>
                <FormLabel>Score Drop per Step</FormLabel>
                <NumberInput
                  size={{ base: 'xs', '2xl': 'sm' }}
                  defaultValue={1}
                  value={event.stepDrop}
                  step={1}
                  min={1}
                  max={1000}
                  onBlur={(e) => {
                    episodeRef.current.events[index].stepDrop = parseInt(
                      e.target.value
                    )
                  }}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormHelperText>
                  The number of points to drop the possible score based on how
                  many &quot;steps&quot; away from the correct guess
                </FormHelperText>
              </FormControl>
            </Box>
          )}
          {/* number curve options */}
          {episodeRef.current.events[index].type === 'numberCurve' && (
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap="1em">
              <FormControl>
                <FormLabel>Answer</FormLabel>
                <Input
                  size={{ base: 'xs', '2xl': 'sm' }}
                  type="number"
                  defaultValue={episodeRef.current.events[index].answer ?? ''}
                  onChange={(e) => {
                    episodeRef.current.events[index].answer = parseInt(
                      e.target.value
                    ).toString()
                    saveEpisodeToLocalStorage()
                  }}
                />
                <FormHelperText>
                  Enter the answer as numeric value
                </FormHelperText>
              </FormControl>
              <FormControl>
                <FormLabel>Partial Score Step Amount </FormLabel>
                <NumberInput
                  size={{ base: 'xs', '2xl': 'sm' }}
                  defaultValue={episodeRef.current.events[index].stepAmount}
                  step={1}
                  min={1}
                  max={1000000}
                  onBlur={(e) => {
                    episodeRef.current.events[index].stepAmount = parseInt(
                      e.target.value
                    )
                  }}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormHelperText>
                  The amount between each level of &quot;closeness&quot; to the
                  correct guess
                </FormHelperText>
              </FormControl>
              <FormControl>
                <FormLabel>Score Drop per Step</FormLabel>
                <NumberInput
                  size={{ base: 'xs', '2xl': 'sm' }}
                  defaultValue={episodeRef.current.events[index].stepDrop}
                  step={1}
                  min={1}
                  max={1000}
                  onBlur={(e) => {
                    episodeRef.current.events[index].stepDrop = parseInt(
                      e.target.value
                    )
                  }}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormHelperText>
                  The number of points to drop the possible score based on how
                  many &quot;steps&quot; away from the correct guess
                </FormHelperText>
              </FormControl>
            </Box>
          )}
          {/* hide captions */}
          <FormControl>
            <FormLabel>Hide Closed Captions / Subtitles</FormLabel>
            <Switch
              isChecked={episodeRef.current.events[index].hideCaptions}
              onChange={(e) => {
                episodeRef.current.events[index].hideCaptions = e.target.checked
                saveEpisodeToLocalStorage()
              }}
            />
            <FormHelperText>
              Sometimes the captions give away the answer, (such as what song is
              playing or what sound effect is made), this let's you hide the
              captions for the duration of this prompt.
            </FormHelperText>
          </FormControl>
        </Box>
      </AccordionPanel>
    )
  }
)
EventPanel.displayName = 'EventPanel'

const Wrap = (props: WrapProps): JSX.Element => {
  return (
    <Center
      minH="100%"
      as={motion.div}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      {...props}
    />
  )
}

const styles = {
  container: {
    backgroundColor: '#181425',
    pos: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%'
  },
  intro: {
    '.chakra-heading, .chakra-form-control, .chakra-form__helper-text': {
      color: 'yellow.400',
      textAlign: 'center'
    },
    '.chakra-text': {
      color: 'yellow.400',
      textAlign: 'left'
    },
    '.chakra-form__error-message': {
      display: 'block',
      textAlign: 'center',
      w: '100%'
    }
  },
  setup: {
    '.chakra-text, .chakra-heading, .chakra-form-control, .chakra-form__helper-text':
      {
        color: 'yellow.400',
        textAlign: 'center'
      },
    '.chakra-form__error-message': {
      display: 'block',
      textAlign: 'center',
      w: '100%'
    }
  },
  dev: {
    height: '100%',
    '.chakra-text, .chakra-heading': {
      color: 'yellow.400',
      textAlign: 'center'
    },
    '.chakra-form-control': {
      mb: '20px',
      'input, select': {
        backgroundColor: 'white'
      }
    }
  }
}
